/**
 * SwitchIn  — N inputs -> 1 output (selector picks which input to forward)
 * SwitchOut — 1 input  -> N outputs (selector picks which output receives it)
 *
 * Count param controls number of ports (2-8). Selector is 0-based.
 * Ports are dynamically added/removed when count changes.
 * dataType defaults to 'any' but can be constrained to enforce connection types.
 *
 * Non-selected connections are disabled in the GraphIndex so the scene builder
 * (and other graph-walking queries) only traverse the active path.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import type { IEndpoint } from '@/runtime/IEndpoint';

const MAX_PORTS = 8;

const DATA_TYPE_OPTIONS = ['any', 'number', 'string', 'boolean', 'array', 'three:geometry', 'three:group', 'three:shape', 'color', 'audio'];

// ─────────────────────────────────────────────────────────────
// SwitchIn — N inputs → 1 output
// ─────────────────────────────────────────────────────────────

const switchInEndpoints = {
  ...params({
    count: { value: 2, min: 2, max: MAX_PORTS, step: 1, label: 'Inputs' },
    selector: { value: 0, min: 0, max: 1, step: 1 },
    dataType: { dataType: 'string', value: 'any', options: DATA_TYPE_OPTIONS, label: 'Type' },
  }),
  ...internals({
    out: { dataType: 'any', isOutput: true, label: 'Output' },
  }),
};

class SwitchInNode extends BaseNode<typeof switchInEndpoints> {
  private inputValues = new Map<string, any>();
  private portCount = 0;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    this.syncPorts();
    this.syncDataType();
    this.syncEdgeDisabled();
  }

  onParamChanged(_payload: Payload, endpointId: string): void {
    if (endpointId === 'count') {
      this.syncPorts();
    }
    if (endpointId === 'dataType') {
      this.syncDataType();
    }
    if (endpointId === 'selector' || endpointId === 'count') {
      this.syncEdgeDisabled();
    }
    this.forward();
  }

  onInternalChanged(payload: Payload, endpointId: string, _source: IEndpoint): void {
    if (endpointId.startsWith('in_')) {
      this.inputValues.set(endpointId, payload?.value ?? payload);
      this.forward();
    }
  }

  private syncPorts(): void {
    const count = this.params.count.value;
    const dt = this.params.dataType?.value ?? 'any';
    const existing = this.deps.endpointApi.getEndpointIds()
      .filter(id => id.startsWith('in_'));
    const existingSet = new Set(existing);

    // Remove excess ports
    for (const id of existing) {
      const idx = parseInt(id.slice(3));
      if (idx >= count) {
        this.removeCustomParam(id);
        this.inputValues.delete(id);
      }
    }

    // Add missing ports
    for (let i = 0; i < count; i++) {
      const id = `in_${i}`;
      if (!existingSet.has(id)) {
        this.addCustomParam(id, {
          dataType: dt,
          dataFlow: 'internal',
          isInput: true,
          label: `In ${i + 1}`,
        });
      }
    }

    this.portCount = count;
    this.setEndpointMetadata('selector', { min: 0, max: count - 1 });
  }

  private syncDataType(): void {
    const dt = this.params.dataType?.value ?? 'any';
    this.setEndpointMetadata('out', { dataType: dt });
    const existing = this.deps.endpointApi.getEndpointIds()
      .filter(id => id.startsWith('in_'));
    for (const id of existing) {
      this.setEndpointMetadata(id, { dataType: dt });
    }
  }

  /** Disable graph edges for non-selected inputs so scene builder only walks the active path */
  private syncEdgeDisabled(): void {
    const count = this.portCount || this.params.count.value;
    const sel = Math.max(0, Math.min(count - 1, Math.floor(this.params.selector.value)));
    const myKey = this.deps.graphIndex.nodeKey(this.deps.instancePath, this.id);

    for (let i = 0; i < count; i++) {
      const endpointId = `in_${i}`;
      // Get ALL reverse edges (including disabled) targeting this endpoint
      const allEdges = this.deps.graphIndex.getAllReverseEdges(myKey);
      for (const edge of allEdges) {
        if (edge.targetEndpointId === endpointId) {
          this.deps.graphIndex.setEdgeDisabled(edge.sourceKey, myKey, endpointId, i !== sel);
        }
      }
    }
  }

  private forward(): void {
    const count = this.portCount || this.params.count.value;
    const sel = Math.max(0, Math.min(count - 1, Math.floor(this.params.selector.value)));
    this.internals.out.set(this.inputValues.get(`in_${sel}`));
  }
}

export const switchInNodeDef: NodeDef<SwitchInNode> = {
  type: 'SwitchIn',
  shortName: 'swIn',
  info: 'N-to-1 selector. Picks which input to forward based on the selector index.',
  domain: Domain.Data,
  endpoints: switchInEndpoints,
  allowAddParam: true,
  ctor: SwitchInNode,
};

// ─────────────────────────────────────────────────────────────
// SwitchOut — 1 input → N outputs
// ─────────────────────────────────────────────────────────────

const switchOutEndpoints = {
  ...params({
    count: { value: 2, min: 2, max: MAX_PORTS, step: 1, label: 'Outputs' },
    selector: { value: 0, min: 0, max: 1, step: 1 },
    dataType: { dataType: 'string', value: 'any', options: DATA_TYPE_OPTIONS, label: 'Type' },
  }),
  ...internals({
    in: { dataType: 'any', isInput: true, label: 'Input' },
  }),
};

class SwitchOutNode extends BaseNode<typeof switchOutEndpoints> {
  private lastInput: any = null;
  private portCount = 0;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    this.syncPorts();
    this.syncDataType();
    this.syncEdgeDisabled();
  }

  onParamChanged(_payload: Payload, endpointId: string): void {
    if (endpointId === 'count') {
      this.syncPorts();
    }
    if (endpointId === 'dataType') {
      this.syncDataType();
    }
    if (endpointId === 'selector' || endpointId === 'count') {
      this.syncEdgeDisabled();
    }
    this.route();
  }

  onInternalChanged(payload: Payload, endpointId: string, _source: IEndpoint): void {
    if (endpointId === 'in') {
      this.lastInput = payload?.value ?? payload;
      this.route();
    }
  }

  private syncPorts(): void {
    const count = this.params.count.value;
    const dt = this.params.dataType?.value ?? 'any';
    const existing = this.deps.endpointApi.getEndpointIds()
      .filter(id => id.startsWith('out_'));
    const existingSet = new Set(existing);

    // Remove excess ports
    for (const id of existing) {
      const idx = parseInt(id.slice(4));
      if (idx >= count) {
        this.removeCustomParam(id);
      }
    }

    // Add missing ports
    for (let i = 0; i < count; i++) {
      const id = `out_${i}`;
      if (!existingSet.has(id)) {
        this.addCustomParam(id, {
          dataType: dt,
          dataFlow: 'internal',
          isOutput: true,
          label: `Out ${i + 1}`,
        });
      }
    }

    this.portCount = count;
    this.setEndpointMetadata('selector', { min: 0, max: count - 1 });
  }

  private syncDataType(): void {
    const dt = this.params.dataType?.value ?? 'any';
    this.setEndpointMetadata('in', { dataType: dt });
    const existing = this.deps.endpointApi.getEndpointIds()
      .filter(id => id.startsWith('out_'));
    for (const id of existing) {
      this.setEndpointMetadata(id, { dataType: dt });
    }
  }

  /** Disable graph edges for non-selected outputs so scene builder only walks the active path */
  private syncEdgeDisabled(): void {
    const count = this.portCount || this.params.count.value;
    const sel = Math.max(0, Math.min(count - 1, Math.floor(this.params.selector.value)));
    const myKey = this.deps.graphIndex.nodeKey(this.deps.instancePath, this.id);

    for (let i = 0; i < count; i++) {
      const endpointId = `out_${i}`;
      // Get ALL forward edges (including disabled) from this endpoint
      const allEdges = this.deps.graphIndex.getAllForwardEdges(myKey);
      for (const edge of allEdges) {
        if (edge.sourceEndpointId === endpointId) {
          this.deps.graphIndex.setEdgeDisabled(myKey, edge.targetKey, edge.targetEndpointId, i !== sel);
        }
      }
    }
  }

  private route(): void {
    const count = this.portCount || this.params.count.value;
    const sel = Math.max(0, Math.min(count - 1, Math.floor(this.params.selector.value)));
    const targetId = `out_${sel}`;
    this.deps.endpointApi.sendOutput(targetId, this.lastInput);
  }
}

export const switchOutNodeDef: NodeDef<SwitchOutNode> = {
  type: 'SwitchOut',
  shortName: 'swOut',
  info: '1-to-N router. Forwards input to the output selected by the selector index.',
  domain: Domain.Data,
  endpoints: switchOutEndpoints,
  allowAddParam: true,
  ctor: SwitchOutNode,
};
